// src/hr/portal/ChecklistsPage.js -- onboarding / offboarding (mockup screen 4, spec §8.5, §16.3 S4).
//   Open checklists: progress, items with their role, owner, due date (red when overdue) and link; a
//   tick records who and when (the ISO evidence) and never changes anything in another system. Who
//   ticked an item comes from HR's change log (the checklist stores a login id only).
//   Templates: one per kind -- label, link, role, owner and days from the start date / last day.
//   Each new checklist is a copy, so editing a template never rewrites a checklist already started.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiAlertCircle, FiArrowDown, FiArrowRight, FiArrowUp, FiCheck, FiClipboard, FiExternalLink, FiPlus, FiTrash2
} from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Btn, Card, EmptyState, Pill, Seg, Skeleton } from '../kit';
import { dateOfTimestamp, shortDate } from '../timeoff/dates';
import { fullDate, fullName, personForEmail, stampLabel } from './portalUtils';
import { FormError } from './FormError';

const KIND_WORDS = { onboarding: 'Onboarding', offboarding: 'Offboarding' };
const ROLES = [
  { value: 'hr', label: 'HR' },
  { value: 'manager', label: 'Manager' },
  { value: 'it', label: 'IT' },
  { value: 'employee', label: 'Employee' }
];
const ROLE_WORDS = Object.fromEntries(ROLES.map((r) => [r.value, r.label]));
// Pages a checklist item may point at, by path, for the link's words.
const LINK_NAMES = [
  ['/badge-management', 'Badge Management'],
  ['/audit-logs', 'user approvals'],
  ['/hr-portal', 'the HR Portal']
];
const OFFSET_MAX = 60;
const MSG = {
  LABEL: 'Give every item a label',
  OFFSET: 'Offset days must be a whole number from -60 to 60',
  LINK: 'Links must start with / or https://'
};

function linkName(link) {
  const path = String(link).split(/[?#]/)[0];
  const hit = LINK_NAMES.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`));
  if (hit) return hit[1];
  try {
    return new URL(link).hostname;
  } catch (err) {
    return 'link';
  }
}

function ItemLink({ link }) {
  const words = linkName(link);
  if (link.startsWith('/')) {
    return <Link to={link} className="hrp-ckp__lk">{`Open ${words}`}<FiArrowRight aria-hidden="true" /></Link>;
  }
  return (
    <a href={link} target="_blank" rel="noopener noreferrer" className="hrp-ckp__lk">
      {`Open ${words}`}<FiExternalLink aria-hidden="true" /><span className="hrp-sr"> (new tab)</span>
    </a>
  );
}

// ---------------------------------------------------------------------------------------------
// Open checklists
// ---------------------------------------------------------------------------------------------

// { "<checklistId>|<itemId>": actor email } for the latest tick of each item, from the change log of
// everyone with an open checklist. A missing answer just leaves "Done <date>".
function useTickers(checklists) {
  const [tickers, setTickers] = useState(new Map());
  const ids = [...new Set((checklists || []).map((c) => c.employee && c.employee.id).filter(Boolean))].sort();
  const idsKey = ids.join(',');
  useEffect(() => {
    if (!idsKey) return undefined;
    let live = true;
    Promise.all(idsKey.split(',').map((employeeId) => hr.admin.changelog({ employeeId, limit: 200 }).catch(() => null)))
      .then((answers) => {
        if (!live) return;
        const next = new Map();
        answers.forEach((answer) => ((answer && answer.changelog) || []).forEach((row) => {
          if (row.action !== 'checklist.item' || !row.after || row.after.done !== true) return;
          const key = `${row.entityId}|${row.after.itemId}`;
          if (!next.has(key)) next.set(key, row.actorEmail); // newest first
        }));
        setTickers(next);
      });
    return () => { live = false; };
  }, [idsKey]);
  return tickers;
}

function anchorText(checklist, today) {
  if (!checklist.anchorDate) return null;
  if (checklist.kind === 'offboarding') return `Last day ${shortDate(checklist.anchorDate)}`;
  return `${checklist.anchorDate > today ? 'Starts' : 'Started'} ${shortDate(checklist.anchorDate)}`;
}

function ChecklistCard({ checklist, today, delay, whoTicked, onTicked }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const items = checklist.items || [];
  const done = items.filter((i) => i.done).length;
  const overdue = items.filter((i) => !i.done && i.dueDate && i.dueDate < today).length;
  const kind = KIND_WORDS[checklist.kind] || 'Checklist';
  const name = (checklist.employee && checklist.employee.name) || '';

  const tick = async (item) => {
    setBusy(item.id);
    setError('');
    try {
      const result = await hr.admin.checklists.setItem(checklist.id, item.id, { done: !item.done });
      onTicked(checklist, item, (result && result.checklist) || null);
    } catch (err) {
      setError(errorText(err, "Couldn't save that. Try again."));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card delay={delay} as="section" aria-label={`${kind} · ${name}`} className="hrp-ck hrp-ckp__card">
      <div className="hrp-card__head">
        <h2 className="hrp-card__title">
          {`${kind} · `}
          {checklist.employee ? <Link to={`../people/${checklist.employee.id}?tab=checklists`} className="hrp-ckp__who">{name}</Link> : name}
        </h2>
        <div className="hrp-ckp__headr">
          {overdue > 0 && <Pill tone="bad">{`${overdue} overdue`}</Pill>}
          <span className="hrp-ckp__anchor">{anchorText(checklist, today)}</span>
        </div>
      </div>
      <div className="hrp-ckp__progrow">
        <div className="hrp-ck__prog" aria-hidden="true"><span style={{ width: `${items.length ? Math.round((done / items.length) * 100) : 0}%` }} /></div>
        <span className="hrp-ck__count">{`${done} of ${items.length} done`}</span>
      </div>
      <ul className="hrp-ck__list">
        {items.map((item) => {
          const late = !item.done && item.dueDate && item.dueDate < today;
          const when = dateOfTimestamp(item.doneAt);
          const who = item.done ? whoTicked(checklist.id, item.id) : null;
          let status = null;
          if (item.done) status = when ? `${who ? `${who} · ` : 'Done '}${shortDate(when)}` : 'Done';
          else if (item.dueDate) status = `${late ? 'Overdue · ' : 'Due '}${fullDate(item.dueDate)}`;
          return (
            <li key={item.id} className="hrp-ck__it hrp-ckp__it" data-done={item.done ? 'true' : undefined}>
              <button
                type="button"
                className="hrp-ck__box"
                role="checkbox"
                aria-checked={!!item.done}
                aria-label={item.label}
                disabled={busy === item.id}
                onClick={() => tick(item)}
              >
                {item.done && <FiCheck aria-hidden="true" />}
              </button>
              <span className="hrp-ck__tx">{item.label}</span>
              <span className="hrp-ckp__meta">
                <Pill tone="mute" className="hrp-ckp__role">{ROLE_WORDS[item.role] || 'HR'}</Pill>
                {item.owner && <span className="hrp-ckp__owner">{item.owner}</span>}
                {item.link && !item.done && <ItemLink link={item.link} />}
                {status && (
                  <span className="hrp-ck__by" data-overdue={late ? 'true' : undefined} title={item.done && item.doneAt ? stampLabel(item.doneAt) : undefined}>
                    {status}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {error && <p className="hrp-al__err" role="alert"><FiAlertCircle aria-hidden="true" />{error}</p>}
    </Card>
  );
}

function OpenChecklists({ me, today, people, state, onReload, onTicked }) {
  const rows = state.rows || [];
  const tickers = useTickers(rows);
  const [mine, setMine] = useState(new Set());
  const all = useMemo(() => (people && people.data && people.data.people) || [], [people]);
  const myEmails = useMemo(() => {
    const self = me && me.employee ? all.find((p) => p.id === me.employee.id) : null;
    return new Set(self ? [self.email, ...(self.emails || [])].filter(Boolean).map((e) => e.toLowerCase()) : []);
  }, [all, me]);

  const whoTicked = (checklistId, itemId) => {
    const key = `${checklistId}|${itemId}`;
    if (mine.has(key)) return 'You';
    const email = tickers.get(key);
    if (!email) return null;
    if (myEmails.has(String(email).toLowerCase())) return 'You';
    const person = personForEmail(all, email);
    return person ? fullName(person) : email;
  };

  const ticked = (checklist, item, next) => {
    const key = `${checklist.id}|${item.id}`;
    setMine((m) => {
      const copy = new Set(m);
      if (item.done) copy.delete(key);
      else copy.add(key);
      return copy;
    });
    onTicked(checklist, next);
  };

  if (!state.rows && state.status === 'error') {
    return (
      <Card>
        <EmptyState compact icon={FiAlertCircle} title="Couldn't load checklists" action={<Btn variant="ghost" size="sm" onClick={onReload}>Try again</Btn>}>
          Something went wrong on our side.
        </EmptyState>
      </Card>
    );
  }
  if (!state.rows) return <Card><Skeleton label="Loading checklists" lines={6} height={16} /></Card>;
  if (rows.length === 0) {
    return (
      <Card>
        <EmptyState icon={FiClipboard} title="No open checklists">
          Onboarding starts when you add someone; offboarding when you set a last day. Both can also be started from a person's profile.
        </EmptyState>
      </Card>
    );
  }
  return (
    <div className="hrp-ckp__grid">
      {rows.map((c, i) => (
        <ChecklistCard key={c.id} checklist={c} today={today} delay={i} whoTicked={whoTicked} onTicked={ticked} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Template editor
// ---------------------------------------------------------------------------------------------

let draftSeq = 0;
const toDraft = (item) => {
  draftSeq += 1;
  return {
    key: `d${draftSeq}`,
    id: item.id || null,
    label: item.label || '',
    link: item.link || '',
    role: item.role || 'hr',
    owner: item.owner || '',
    offset: String(Number(item.offsetDays) || 0)
  };
};
const comparable = (items) => JSON.stringify((items || []).map((i) => [i.id, i.label, i.link, i.role, i.owner, i.offset]));

function itemProblem(item) {
  if (!item.label.trim()) return MSG.LABEL;
  const offset = item.offset.trim();
  if (offset !== '' && (!/^-?\d+$/.test(offset) || Math.abs(Number(offset)) > OFFSET_MAX)) return MSG.OFFSET;
  const link = item.link.trim();
  if (link && !/^\/(?![/\\])/.test(link) && !/^https:\/\/[^/\s]+/.test(link)) return MSG.LINK;
  return null;
}

function TemplateRow({ item, index, count, anchorWord, onChange, onMove, onRemove }) {
  const n = index + 1;
  const problem = itemProblem(item);
  const errId = `hrp-tpl-err-${item.key}`;
  const described = problem ? { 'aria-describedby': errId } : {};
  return (
    <li className="hrp-tpl__it" data-invalid={problem ? 'true' : undefined}>
      <span className="hrp-tpl__n" aria-hidden="true">{n}</span>
      <div className="hrp-tpl__fields">
        <input className="hrp-input hrp-tpl__label" aria-label={`Item ${n} label`} value={item.label} maxLength={120}
          placeholder="What needs doing" onChange={(e) => onChange({ label: e.target.value })} aria-invalid={problem === MSG.LABEL ? 'true' : undefined} {...described} />
        <select className="hrp-select hrp-tpl__role" aria-label={`Item ${n} role`} value={item.role} onChange={(e) => onChange({ role: e.target.value })}>
          {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <input className="hrp-input hrp-tpl__owner" aria-label={`Item ${n} owner`} value={item.owner} maxLength={80}
          placeholder="Owner (optional)" onChange={(e) => onChange({ owner: e.target.value })} />
        <label className="hrp-tpl__off">
          <input className="hrp-input" type="number" step={1} min={-OFFSET_MAX} max={OFFSET_MAX} inputMode="numeric"
            aria-label={`Item ${n} days from ${anchorWord}`} value={item.offset} onChange={(e) => onChange({ offset: e.target.value })}
            aria-invalid={problem === MSG.OFFSET ? 'true' : undefined} {...described} />
          <span aria-hidden="true">days</span>
        </label>
        <input className="hrp-input hrp-tpl__link" aria-label={`Item ${n} link`} value={item.link} maxLength={300}
          placeholder="Link (optional)" onChange={(e) => onChange({ link: e.target.value })}
          aria-invalid={problem === MSG.LINK ? 'true' : undefined} {...described} />
      </div>
      <div className="hrp-tpl__acts">
        <button type="button" className="hrp-iconbtn hrp-tpl__btn" aria-label={`Move item ${n} up`} disabled={index === 0} onClick={() => onMove(-1)}><FiArrowUp aria-hidden="true" /></button>
        <button type="button" className="hrp-iconbtn hrp-tpl__btn" aria-label={`Move item ${n} down`} disabled={index === count - 1} onClick={() => onMove(1)}><FiArrowDown aria-hidden="true" /></button>
        <button type="button" className="hrp-iconbtn hrp-tpl__btn hrp-ent__btn--del" aria-label={`Remove item ${n}`} disabled={count === 1} onClick={onRemove}><FiTrash2 aria-hidden="true" /></button>
      </div>
      {problem && <p id={errId} className="hrp-tpl__err"><FiAlertCircle aria-hidden="true" />{problem}</p>}
    </li>
  );
}

function TemplateEditor({ onToast }) {
  const [kind, setKind] = useState('onboarding');
  const [templates, setTemplates] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const added = useRef(false);

  const load = useCallback(async (which) => {
    setTemplates((t) => ({ ...t, [which]: { ...(t[which] || {}), status: 'loading' } }));
    try {
      const body = await hr.admin.checklistTemplates.get(which);
      const items = ((body && body.items) || []).map(toDraft);
      setTemplates((t) => ({ ...t, [which]: { status: 'ready', saved: items, draft: items } }));
    } catch (err) {
      setTemplates((t) => ({ ...t, [which]: { ...(t[which] || {}), status: 'error' } }));
    }
  }, []);
  useEffect(() => { load('onboarding'); load('offboarding'); }, [load]);

  const tpl = templates[kind] || { status: 'loading' };
  const draft = tpl.draft || [];
  const dirty = tpl.status === 'ready' && comparable(draft) !== comparable(tpl.saved);
  const problems = draft.filter((i) => itemProblem(i)).length;
  const anchorWord = kind === 'onboarding' ? 'start date' : 'last day';

  const setDraft = (next) => {
    setTemplates((t) => ({ ...t, [kind]: { ...t[kind], draft: next } }));
    setError('');
  };
  const change = (i, patch) => setDraft(draft.map((item, at) => (at === i ? { ...item, ...patch } : item)));
  const move = (i, by) => {
    const next = [...draft];
    const [item] = next.splice(i, 1);
    next.splice(i + by, 0, item);
    setDraft(next);
  };
  const addItem = () => {
    added.current = true;
    setDraft([...draft, toDraft({ role: 'hr', offsetDays: 0 })]);
  };

  // Focus the new item's label once it's on screen.
  const listRef = useRef(null);
  useEffect(() => {
    if (!added.current || !listRef.current) return;
    added.current = false;
    const inputs = listRef.current.querySelectorAll('.hrp-tpl__label');
    if (inputs.length) inputs[inputs.length - 1].focus();
  }, [draft.length]);

  const save = async (e) => {
    e.preventDefault();
    if (!dirty || problems || busy) return;
    const items = draft.map((i) => {
      const item = {
        label: i.label.trim(),
        link: i.link.trim(),
        role: i.role,
        owner: i.owner.trim(),
        offsetDays: i.offset.trim() === '' ? 0 : Number(i.offset.trim())
      };
      return i.id ? { id: i.id, ...item } : item;
    });
    setBusy(true);
    setError('');
    try {
      const body = await hr.admin.checklistTemplates.put(kind, { items });
      const saved = ((body && body.items) || []).map(toDraft);
      setTemplates((t) => ({ ...t, [kind]: { status: 'ready', saved, draft: saved } }));
      onToast(`${KIND_WORDS[kind]} template saved. Checklists already started don't change.`);
    } catch (err) {
      setError(errorText(err, "Couldn't save the template. Try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      delay={0}
      as="section"
      aria-label={`${KIND_WORDS[kind]} template`}
      className="hrp-tpl"
    >
      <div className="hrp-card__head hrp-tpl__head">
        <h2 className="hrp-card__title">{`${KIND_WORDS[kind]} template`}</h2>
        <Seg label="Template" size="sm" options={[{ value: 'onboarding', label: 'Onboarding' }, { value: 'offboarding', label: 'Offboarding' }]} value={kind} onChange={setKind} />
      </div>
      <p className="hrp-tpl__lead">
        {`Each new ${kind} checklist is a copy of this list. Days count from the ${anchorWord} (negative = before). Editing it never changes a checklist already started.`}
      </p>
      {tpl.status === 'error' && !tpl.draft && (
        <EmptyState compact icon={FiAlertCircle} title="Couldn't load the template" action={<Btn variant="ghost" size="sm" onClick={() => load(kind)}>Try again</Btn>}>
          Something went wrong on our side.
        </EmptyState>
      )}
      {tpl.status === 'loading' && !tpl.draft && <Skeleton label="Loading the template" lines={5} height={18} />}
      {tpl.draft && (
        <form onSubmit={save} noValidate>
          <div className="hrp-tpl__cols" aria-hidden="true">
            <span>Item</span><span>Role</span><span>Owner</span><span>{`Days from ${anchorWord}`}</span><span>Link</span>
          </div>
          <ol className="hrp-tpl__list" ref={listRef}>
            {draft.map((item, i) => (
              <TemplateRow
                key={item.key}
                item={item}
                index={i}
                count={draft.length}
                anchorWord={anchorWord}
                onChange={(patch) => change(i, patch)}
                onMove={(by) => move(i, by)}
                onRemove={() => setDraft(draft.filter((_, at) => at !== i))}
              />
            ))}
          </ol>
          <div className="hrp-tpl__add">
            <Btn variant="ghost" size="sm" icon={FiPlus} onClick={addItem} disabled={draft.length >= 50}>Add item</Btn>
            <span className="hrp-dim">Links point at a page here (like /badge-management) or an https address. Ticking never changes the other system.</span>
          </div>
          <FormError>{error}</FormError>
          <div className="hrp-tpl__foot">
            {problems > 0 && <span className="hrp-tpl__sum" role="status">{`${problems} ${problems === 1 ? 'item needs' : 'items need'} a fix`}</span>}
            {dirty && <Btn variant="ghost" size="sm" onClick={() => setDraft(tpl.saved)} disabled={busy}>Discard changes</Btn>}
            <Btn type="submit" size="sm" icon={FiCheck} loading={busy} disabled={!dirty || problems > 0}>Save template</Btn>
          </div>
        </form>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------------------------

export function ChecklistsPage({ me, today, people, onToast }) {
  const [view, setView] = useState('open');
  const [state, setState] = useState({ status: 'loading', rows: null });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      const rows = await hr.admin.checklists.list({ open: 1 });
      setState({ status: 'ready', rows: Array.isArray(rows) ? rows : [] });
    } catch (err) {
      setState((s) => ({ ...s, status: 'error' }));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // A ticked item updates its checklist in place; a finished checklist stays on screen until the
  // next visit, marked complete, so the last tick doesn't make the card vanish under the pointer.
  const ticked = (checklist, next) => {
    if (!next) { load(); return; }
    setState((s) => ({
      ...s,
      rows: (s.rows || []).map((c) => (c.id === checklist.id ? { ...c, ...next, employee: c.employee } : c))
    }));
    if (next.completedAt) onToast(`${KIND_WORDS[checklist.kind] || 'Checklist'} for ${checklist.employee ? checklist.employee.name : 'them'} is complete`);
  };

  const open = state.rows ? state.rows.length : null;
  return (
    <div className="hrp-page hrp-ckp">
      <div className="hrp-page-head">
        <div>
          <h1 className="hrp-h1">Checklists</h1>
          <p className="hrp-sub">Each tick records who and when. Ticking an item never changes anything in another system.</p>
        </div>
      </div>
      <div className="hrp-chips hrp-ckp__chips" role="group" aria-label="Show">
        <button type="button" className="hrp-chip" aria-pressed={view === 'open'} onClick={() => setView('open')}>
          Open checklists
          {open > 0 && <>{' '}<span className="hrp-pp-chipn">{open}</span></>}
        </button>
        <button type="button" className="hrp-chip" aria-pressed={view === 'templates'} onClick={() => setView('templates')}>Templates</button>
      </div>
      {view === 'open'
        ? <OpenChecklists me={me} today={today} people={people} state={state} onReload={load} onTicked={ticked} />
        : <TemplateEditor onToast={onToast} />}
    </div>
  );
}

export default ChecklistsPage;
